import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { ConfirmationDialogComponent, ConfirmationDialogType } from "@comunicare/comunicare-dashboard-modals";
import { Tools } from "@comunicare/comunicare-helper";
import {
  FHIRHelper,
  ICareplan,
  IPatient,
  IPatientUser,
  IPrescription,
  IPrescriptionInCareplan,
  IPricingConfig,
  IRelatedPerson,
  Reference,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";
import { IGroup } from "../components/patient-careplans/patient-careplans.component";
import { Base64Helper } from "../helpers/Base64Helper";
import { PatientService } from "./patient.service";

@Injectable({
  providedIn: "root",
})
export class PatientRegistrationService {
  public go2patientPage: boolean;
  public patientSaved: IPatient;
  public previousUrl: string;
  public isForceUpdateUser: boolean;
  public isCreation: boolean;
  public directToCareplan: boolean;
  public source: string;
  public linkData: {
    name: string;
    value: string | number;
  }[];
  public patientUser: IPatientUser;
  public careplan: ICareplan;
  public careplans: IGroup[];
  public createNewCareplan = true;
  public includeBirthdateAndGenderForm = false;
  public registration: boolean;
  public healthcareService: Reference;
  public isLastStep = false;
  public careplanPrescriptions: IPrescriptionInCareplan[] = [];
  public careplanRef: Reference;
  public prescription: IPrescription;
  public directToPrescription: boolean;
  public prescriptionConfig: IPricingConfig;
  public isPendingPrescriptionEdit: boolean;

  public onPatientCreated$ = new Subject<{ patientSaved: IPatient; forceUpdateUser?: string }>();
  public onRelatedCreated$ = new Subject<IRelatedPerson>();
  public onNeedReferentChanged$ = new Subject<boolean>();
  public onNeedCareplanChanged$ = new Subject<boolean>();
  public onNeedPrescriptionChanged$ = new Subject<boolean>();
  public onCareplanCreatedOrUpdated$ = new Subject<string>();
  public onPrescriptionCreated$ = new Subject<IPrescription>();
  public onNeedVitalSignsChanged$ = new Subject<boolean>();

  public onNextStep$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog,
    private patientService: PatientService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  public onFinish(): void {
    this.translateService
      .get("common.saveSuccess")
      .pipe(first())
      .subscribe((trans) => {
        if (!this.isForceUpdateUser && !this.directToCareplan) {
          if (this.patientSaved?.noSms) {
            trans += " Authocode : " + this.patientService.computeAuthCode(this.patientSaved);
          }
          const dialog = this.dialog.open(ConfirmationDialogComponent, {
            disableClose: true,
            data: {
              message: trans,
              type: ConfirmationDialogType.SUCCESS,
              showButtons: this.go2patientPage === undefined,
            },
          });

          dialog.afterClosed().subscribe((res) => {
            // if preference is already set
            if (this.go2patientPage === true) {
              this.navigateToPatientPage();
            } else {
              // redirect if wanted
              if (res) {
                this.navigateToPatientPage();
              } else {
                this.back();
              }
            }
          });
        } else {
          this.back();
          this.snackBar.open(trans, "ok", { duration: 3000 });
        }
      });
  }

  private navigateToPatientPage(): void {
    const patient = Tools.isDefined(this.patientSaved) ? this.patientSaved : this.patientUser.patient;
    const patientId = FHIRHelper.getMainIdentifier(patient).value;
    const b64Id = Base64Helper.utf8_to_b64(patientId);
    this.reset(); // reset before navigation
    this.router.navigateByUrl(`/patient;id=${b64Id}`, { state: undefined });
  }

  public back(): void {
    const url = this.previousUrl ?? "/";
    this.reset(); // reset before navigation
    this.router.navigateByUrl(url);
  }

  public reset(): void {
    this.go2patientPage = undefined;
    this.patientSaved = undefined;
    this.isForceUpdateUser = undefined;
    this.isCreation = undefined;
    this.directToCareplan = undefined;
    this.directToPrescription = undefined;
    this.source = undefined;
    this.linkData = undefined;
    this.patientUser = undefined;
    this.careplan = undefined;
    this.careplans = undefined;
    this.createNewCareplan = true;
    this.includeBirthdateAndGenderForm = false;
    this.registration = undefined;
    this.healthcareService = undefined;
    this.isLastStep = false;
    this.careplanPrescriptions = [];
    this.careplanRef = undefined;
    this.prescription = undefined;
    this.prescriptionConfig = undefined;
    this.isPendingPrescriptionEdit = undefined;
  }
}
